import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDebounce } from 'react-use';
import styled, { css } from 'styled-components';

import type { RangeValues } from '#src/atoms/inputs/RangeSlider/index.tsx';
import { RangeSlider } from '#src/atoms/inputs/RangeSlider/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { clamp } from '#src/lib/math.ts';
import type { Color } from '#src/style/types.ts';

export const InputsLine = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--unit);
  margin-top: var(--double-unit);
`;

export const StyledLabel = styled.label`
  position: relative;
  flex: 1;
`;

export const StyledInput = styled.input<{
  withIcon: boolean;
  withMinMax?: boolean;
  borderColor: Color;
}>`
  height: 40px;
  border-radius: var(--radius-xs);
  border: 1px solid ${({ borderColor }) => borderColor};
  padding: 0 var(--double-unit);
  ${({ withIcon }) =>
    withIcon &&
    css`
      padding-left: calc(5 * var(--unit));
    `}
  ${({ withMinMax }) =>
    withMinMax &&
    css`
      padding-left: calc(6 * var(--unit));
    `}
  font-size: 16px;
  font-weight: 700;
  color: var(--c-white);
  background-color: var(--c-nd-100);
  width: 100%;
`;

const LabelContent = styled.span`
  position: absolute;
  line-height: var(--lh-20);
  top: calc(50% - var(--lh-20) / 2);
  left: var(--double-unit);
`;

export const IconWrapper = styled(Horizontal).attrs({ gap: 0 })`
  position: absolute;
  width: var(--double-and-a-half-unit);
  height: 20px;
  top: 50%;
  transform: translateY(-50%);
  left: var(--double-unit);
  > * {
    max-width: 100%;
  }
`;

export const Separator = styled.span`
  display: flex;
  align-items: center;
  color: var(--c-white);
`;

type Props = {
  id: string;
  min?: number;
  max?: number;
  rangeValues: RangeValues;
  icon?: ReactNode;
  /** Called when moving sliders */
  onChange?: (values: RangeValues) => void;
  /** Called when slider has been released */
  onChangeCommitted?: (values: RangeValues) => void;
  disabled?: boolean;
  inputBorderColor?: Color;
};

const clampRangeValues = (values: RangeValues, min: number, max: number) => ({
  low: clamp(values.low, min, max),
  high: clamp(values.high, min, max),
});

const isRangeValuesEqual = (values1: RangeValues, values2: RangeValues) => {
  return values1.low === values2.low && values1.high === values2.high;
};

export const RangeSliderWithInputs = ({
  id,
  min = 0,
  max = 100,
  rangeValues,
  icon,
  onChange,
  onChangeCommitted,
  disabled,
  inputBorderColor = 'var(--c-nd-100)',
}: Props) => {
  const clampedRangeValues = clampRangeValues(rangeValues, min, max);
  const [inputValues, setInputValues] = useState<{
    low: string;
    high: string;
  }>({
    low: rangeValues.low.toString(),
    high: rangeValues.high.toString(),
  });
  const [prevClampedRangeValues, setPrevClampedRangeValues] =
    useState<RangeValues>(clampedRangeValues);
  if (!isRangeValuesEqual(prevClampedRangeValues, clampedRangeValues)) {
    setInputValues({
      low: clampedRangeValues.low.toString(),
      high: clampedRangeValues.high.toString(),
    });
    setPrevClampedRangeValues(clampedRangeValues);
  }

  const onValuesChange = (values: RangeValues, commit = false) => {
    const clampedValues = clampRangeValues(values, min, max);
    const reorderedClampedValues = {
      low: Math.min(clampedValues.low, clampedValues.high),
      high: Math.max(clampedValues.low, clampedValues.high),
    };
    setInputValues({
      low: reorderedClampedValues.low.toString(),
      high: reorderedClampedValues.high.toString(),
    });

    onChange?.(reorderedClampedValues);
    if (commit) {
      onChangeCommitted?.(reorderedClampedValues);
    }
  };

  useDebounce(
    () => {
      if (
        +inputValues.low !== rangeValues.low ||
        +inputValues.high !== rangeValues.high
      ) {
        onValuesChange(
          { low: +inputValues.low, high: +inputValues.high },
          true
        );
      }
    },
    1000,
    [rangeValues, inputValues]
  );

  return (
    <div>
      <RangeSlider
        rangeValues={rangeValues}
        onChange={onValuesChange}
        onMouseUp={(values: RangeValues) => onValuesChange(values, true)}
        min={min}
        max={max}
        disabled={disabled}
      />
      <InputsLine>
        <StyledLabel>
          <LabelContent>
            <LabelS as="span" color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Min" />
            </LabelS>
          </LabelContent>
          {!!icon && <IconWrapper>{icon}</IconWrapper>}
          <StyledInput
            id={`${id}-input1`}
            type="number"
            max={max}
            value={inputValues.low}
            onChange={e =>
              setInputValues(old => ({ ...old, low: e.target.value }))
            }
            withIcon={!!icon}
            withMinMax={typeof max === 'number'}
            disabled={disabled}
            borderColor={inputBorderColor}
          />
        </StyledLabel>
        <Separator>-</Separator>
        <StyledLabel>
          <LabelContent>
            <LabelS as="span" color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Max" />
            </LabelS>
          </LabelContent>
          {!!icon && <IconWrapper>{icon}</IconWrapper>}
          <StyledInput
            id={`${id}-input2`}
            type="number"
            min={min}
            value={inputValues.high}
            onChange={e =>
              setInputValues(old => ({ ...old, high: e.target.value }))
            }
            withIcon={!!icon}
            withMinMax={typeof min === 'number'}
            disabled={disabled}
            borderColor={inputBorderColor}
          />
        </StyledLabel>
      </InputsLine>
    </div>
  );
};
